import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../services/firebase";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faMapMarkerAlt,
  faPaperPlane,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faUser,
  faChevronDown,
  faCalendarAlt,
  faChevronUp,
  faComments,
} from "@fortawesome/free-solid-svg-icons";
import "../../../styles/pages/contact.css";
import { faInstagram } from "@fortawesome/free-brands-svg-icons";

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    subject: "Kontakt forma sa web stranice",
    message: "",
  });

  const [successMessage, setSuccessMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const formRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  const faqItems: FAQItem[] = [
    {
      question: "Kako mogu rezervirati termin?",
      answer:
        "Termin možete rezervirati putem našeg online sustava za rezervacije, ili slanjem emaila. Preporučujemo rezervaciju barem tjedan dana unaprijed za popularne tretmane.",
    },
    {
      question: "Što ako moram otkazati rezervaciju?",
      answer:
        "Razumijemo da se planovi mijenjaju. Molimo vas da otkažete rezervaciju najmanje 24 sata unaprijed kako bismo mogli ponuditi termin drugim klijentima. Otkazivanje se može izvršiti telefonom ili emailom.",
    },
    {
      question: "Koje načine plaćanja prihvaćate?",
      answer: "Za sada prihvaćamo samo gotovinu kao način plaćanja.",
    },
  ];

  // Efekt za animaciju elemenata na scroll
  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll(".animate-on-scroll");
      elements.forEach((element) => {
        const position = element.getBoundingClientRect();
        // Provjera je li element vidljiv na ekranu
        if (position.top < window.innerHeight - 100) {
          element.classList.add("animate-visible");
        }
      });
    };

    // Inicijalna provjera vidljivih elemenata
    handleScroll();

    // Dodavanje i uklanjanje event listenera
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    const sendEmail = httpsCallable(functions, "sendEmail");
    try {
      await sendEmail(formData);
      setSuccessMessage(
        "Vaša poruka je uspješno poslana! Hvala što ste nas kontaktirali."
      );
      setFormData({
        name: "",
        email: "",
        subject: "Kontakt forma sa web stranice",
        message: "",
      });

      // Scroll to form to show success message
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } catch (error: any) {
      console.error("Greška pri slanju poruke:", error.message, error.response);
      setErrorMessage(
        "Došlo je do pogreške prilikom slanja poruke. Molimo pokušajte ponovno."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const scrollToMap = () => {
    if (mapRef.current) {
      mapRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <div className="contact-page">
      {/* Hero sekcija */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-content">
            <h1 className="contact-hero-title">Kontaktirajte nas</h1>
            <p className="contact-hero-description">
              Javite nam se s povjerenjem! Odgovorit ćemo u najkraćem mogućem
              roku.
            </p>
            <div className="contact-hero-buttons">
              <a href="#contact-form" className="hero-button primary">
                <FontAwesomeIcon icon={faEnvelope} />
                <span>Pošaljite upit</span>
              </a>
              <button onClick={scrollToMap} className="hero-button secondary">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <span>Lokacija</span>
              </button>
            </div>
          </div>
        </div>
        <div className="hero-wave">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
            <path
              fill="#ffffff"
              fillOpacity="1"
              d="M0,256L48,240C96,224,192,192,288,181.3C384,171,480,181,576,197.3C672,213,768,235,864,224C960,213,1056,171,1152,154.7C1248,139,1344,149,1392,154.7L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </div>
      </section>

      {/* Glavna sekcija kontakta */}
      <section className="contact-main">
        <div className="container">
          <div className="contact-content">
            {/* Kontakt informacije */}
            <div className="contact-info animate-on-scroll">
              <div className="section-header">
                <span className="section-subtitle">Naša lokacija</span>
                <h2 className="section-title">Gdje nas možete pronaći</h2>
              </div>

              <div className="contact-cards">
                <div className="contact-card">
                  <div className="card-icon">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </div>
                  <h3>Adresa</h3>
                  <p>
                    Beauty Concept Veronika
                    <br />
                    Ulica grada Vukovara 226G
                    <br />
                    10000 Zagreb
                  </p>
                </div>

                <div className="contact-card">
                  <div className="card-icon">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </div>
                  <h3>Email</h3>
                  <p>
                    <a href="mailto:info@beautyconceptveronika.hr">
                      info@beautyconceptveronika.hr
                    </a>
                  </p>
                </div>

                <div className="contact-card">
                  <div className="card-icon">
                    <FontAwesomeIcon icon={faInstagram} />
                  </div>
                  <h3>Instagram</h3>
                  <p>
                    <a
                      href="https://instagram.com/beautyconceptveronika"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      @beautyconceptveronika
                    </a>
                  </p>
                </div>
              </div>

              <div ref={mapRef} className="contact-map">
                <iframe
                  title="Google Maps"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2781.535537285411!2d15.98948257755327!3d45.80053207108147!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4765d6452f411f55%3A0x20417f441a23cb63!2sUl.%20grada%20Vukovara%20226G%2C%2010000%2C%20Zagreb!5e0!3m2!1shr!2shr!4v1733301359883!5m2!1shr!2shr"
                  width="100%"
                  height="350"
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            </div>

            {/* Kontakt forma */}
            <div
              id="contact-form"
              ref={formRef}
              className="contact-form-container animate-on-scroll"
            >
              <div className="section-header">
                <span className="section-subtitle">Pošaljite poruku</span>
                <h2 className="section-title">Kontaktirajte nas</h2>
              </div>

              {successMessage && (
                <div className="message success-message">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <p>{successMessage}</p>
                </div>
              )}

              {errorMessage && (
                <div className="message error-message">
                  <FontAwesomeIcon icon={faExclamationCircle} />
                  <p>{errorMessage}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">
                    Ime i prezime <span className="required">*</span>
                  </label>
                  <div className="input-with-icon">
                    <FontAwesomeIcon icon={faUser} className="input-icon" />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Unesite vaše ime"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    Email adresa <span className="required">*</span>
                  </label>
                  <div className="input-with-icon">
                    <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Unesite vaš email"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    Poruka <span className="required">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Unesite vašu poruku"
                    rows={4}
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                      <span>Šaljem...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPaperPlane} />
                      <span>Pošalji poruku</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-subtitle">Česta pitanja</span>
            <h2 className="section-title">Što klijenti najčešće pitaju</h2>
            <p className="section-description">
              Pronađite odgovore na najčešća pitanja o našim uslugama i
              poslovanju. Ako ne pronađete odgovor koji tražite, slobodno nas
              kontaktirajte.
            </p>
          </div>

          <div className="faq-container animate-on-scroll">
            {faqItems.map((item, index) => (
              <div
                key={index}
                className={`faq-item ${openFAQ === index ? "active" : ""}`}
              >
                <div className="faq-question" onClick={() => toggleFAQ(index)}>
                  <div className="faq-icon">
                    <FontAwesomeIcon icon={faComments} />
                  </div>
                  <h3>{item.question}</h3>
                  <div className="faq-toggle">
                    <FontAwesomeIcon
                      icon={openFAQ === index ? faChevronUp : faChevronDown}
                    />
                  </div>
                </div>
                <div
                  className={`faq-answer ${openFAQ === index ? "show" : ""}`}
                >
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="contact-cta-section">
        <div className="container">
          <div className="contact-cta-content animate-on-scroll">
            <h2>Rezervirajte svoj termin već danas!</h2>
            <p>
              Prepustite se rukama naših stručnjaka i doživite jedinstveno
              iskustvo ljepote i opuštanja.
            </p>
            <div className="cta-buttons">
              <Link to="/booking" className="cta-button">
                <FontAwesomeIcon icon={faCalendarAlt} className="button-icon" />
                Rezerviraj svoj termin
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
